import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  LayoutDashboard, Building2, Users, FileText, LogOut, 
  Plus, Trash2, Edit, Eye, Download, Mail, ChevronRight,
  Home, ClipboardList, Globe, ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propertyModalOpen, setPropertyModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      // Not authenticated - show login
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!location.state?.user) {
      checkAuth();
    }
  }, [location.state, checkAuth]);

  useEffect(() => {
    if (user) {
      fetchStats();
      fetchLeads();
      fetchProperties();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      logger.error("Failed to fetch stats:", error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`, { withCredentials: true });
      setLeads(response.data);
    } catch (error) {
      logger.error("Failed to fetch leads:", error);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`, { withCredentials: true });
      setProperties(response.data);
    } catch (error) {
      logger.error("Failed to fetch properties:", error);
    }
  };

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      logger.error("Logout error:", error);
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;
    try {
      await axios.delete(`${API}/properties/${propertyId}`, { withCredentials: true });
      toast.success("Property deleted");
      fetchProperties();
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  const handleUpdateLeadStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status }, { withCredentials: true });
      toast.success("Lead status updated");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to update lead");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <motion.div 
          className="bg-white p-12 shadow-float border border-slate-100 text-center max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-3xl font-bold text-[#1A1A2E] mb-4">Admin Dashboard</h1>
          <p className="text-slate-600 mb-8">Sign in with your Google account to access the admin dashboard.</p>
          <Button onClick={handleLogin} className="btn-primary w-full py-6 rounded-sm" data-testid="admin-login-btn">
            Sign in with Google
          </Button>
          <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-[#1A1A2E] mt-6 text-sm">
            <Home className="w-4 h-4" />
            Return to Website
          </Link>
        </motion.div>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white p-12 shadow-float border border-slate-100 text-center max-w-md">
          <h1 className="text-2xl font-bold text-[#1A1A2E] mb-4">Access Denied</h1>
          <p className="text-slate-600 mb-8">You don't have admin privileges. Please contact an administrator.</p>
          <div className="space-y-3">
            <Button onClick={handleLogout} variant="outline" className="w-full">
              Sign Out
            </Button>
            <Link to="/">
              <Button variant="ghost" className="w-full">Return to Website</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#1A1A2E] text-white flex flex-col">
        <div className="p-6 border-b border-slate-700">
          <Link to="/" className="font-bold text-xl">
            1031<span className="text-[#60A5FA]">Condo</span>
          </Link>
          <p className="text-xs text-slate-400 mt-1">Admin Dashboard</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {[
            { id: "dashboard", icon: LayoutDashboard, label: "Dashboard" },
            { id: "properties", icon: Building2, label: "Properties" },
            { id: "scraper", icon: Globe, label: "Listing Importer" },
            { id: "reports", icon: ClipboardList, label: "Client Reports" },
            { id: "audits", icon: ShieldCheck, label: "Inventory Audit" },
            { id: "leads", icon: Users, label: "Leads" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (item.id === "properties") {
                  navigate("/admin/properties");
                } else if (item.id === "reports") {
                  navigate("/admin/reports");
                } else if (item.id === "scraper") {
                  navigate("/admin/scraper");
                } else if (item.id === "audits") {
                  navigate("/admin/audits");
                } else {
                  setActiveTab(item.id);
                }
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-colors ${
                activeTab === item.id 
                  ? "bg-white/10 text-white" 
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`admin-nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-4">
            {user.picture && (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.name}</p>
              <p className="text-xs text-slate-400 truncate">{user.email}</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="ghost" className="w-full text-slate-400 hover:text-white justify-start">
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {activeTab === "dashboard" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h1 className="text-3xl font-bold text-[#1A1A2E] mb-8">Dashboard</h1>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[
                { label: "Total Leads", value: stats?.total_leads || 0, icon: Users },
                { label: "New Leads", value: stats?.new_leads || 0, icon: Mail },
                { label: "Properties", value: stats?.total_properties || 0, icon: Building2 },
                { label: "Guide Downloads", value: stats?.guide_downloads || 0, icon: Download },
              ].map((stat, idx) => (
                <div key={idx} className="bg-white p-6 border border-slate-100 shadow-soft">
                  <div className="flex items-center justify-between mb-4">
                    <stat.icon className="w-6 h-6 text-[#0066FF]" />
                    <span className="text-3xl font-bold text-[#1A1A2E]">{stat.value}</span>
                  </div>
                  <p className="text-sm text-slate-600">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Recent Leads */}
            <div className="bg-white border border-slate-100 shadow-soft">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-xl font-medium text-[#1A1A2E]">Recent Leads</h2>
                <Button variant="ghost" onClick={() => setActiveTab("leads")} className="text-[#0066FF]">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="divide-y divide-slate-100">
                {leads.slice(0, 5).map((lead) => (
                  <div key={lead.lead_id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-[#1A1A2E]">{lead.name}</p>
                      <p className="text-sm text-slate-500">{lead.email}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      lead.status === "new" ? "bg-blue-100 text-blue-700" :
                      lead.status === "contacted" ? "bg-yellow-100 text-yellow-700" :
                      lead.status === "qualified" ? "bg-green-100 text-green-700" :
                      "bg-slate-100 text-slate-700"
                    }`}>
                      {lead.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "properties" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl font-bold text-[#1A1A2E]">Properties</h1>
              <PropertyModal 
                open={propertyModalOpen} 
                onOpenChange={setPropertyModalOpen}
                property={editingProperty}
                onSuccess={() => { fetchProperties(); setEditingProperty(null); }}
              />
            </div>

            <div className="bg-white border border-slate-100 shadow-soft overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Property</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Market</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Price</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {properties.map((prop) => (
                    <tr key={prop.property_id} className="hover:bg-slate-50">
                      <td className="p-4">
                        <p className="font-medium text-[#1A1A2E]">{prop.title}</p>
                        <p className="text-sm text-slate-500">{prop.location}</p>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 text-xs font-medium bg-slate-100 rounded">
                          {prop.market === "nyc" ? "NYC" : "Miami"}
                        </span>
                      </td>
                      <td className="p-4 font-medium">${prop.price.toLocaleString()}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          prop.status === "available" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"
                        }`}>
                          {prop.status}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => { setEditingProperty(prop); setPropertyModalOpen(true); }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteProperty(prop.property_id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {activeTab === "leads" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h1 className="text-3xl font-bold text-[#1A1A2E] mb-8">Leads</h1>

            <div className="bg-white border border-slate-100 shadow-soft overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Contact</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Source</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Target Market</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.map((lead) => (
                    <tr key={lead.lead_id} className="hover:bg-slate-50">
                      <td className="p-4">
                        <p className="font-medium text-[#1A1A2E]">{lead.name}</p>
                        <p className="text-sm text-slate-500">{lead.email}</p>
                        {lead.phone && <p className="text-sm text-slate-400">{lead.phone}</p>}
                      </td>
                      <td className="p-4 text-sm text-slate-600 capitalize">{lead.source?.replace(/_/g, " ")}</td>
                      <td className="p-4 text-sm text-slate-600 capitalize">{lead.target_market || "-"}</td>
                      <td className="p-4">
                        <Select 
                          value={lead.status} 
                          onValueChange={(v) => handleUpdateLeadStatus(lead.lead_id, v)}
                        >
                          <SelectTrigger className="w-32 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="qualified">Qualified</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4 text-sm text-slate-500">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}

// Property Modal Component
function PropertyModal({ open, onOpenChange, property, onSuccess }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    location: "",
    market: "nyc",
    bedrooms: "",
    bathrooms: "",
    sqft: "",
    building_name: "",
    address: "",
    amenities: "",
    images: "",
    featured: false
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (property) {
      setFormData({
        title: property.title,
        description: property.description,
        price: property.price.toString(),
        location: property.location,
        market: property.market,
        bedrooms: property.bedrooms.toString(),
        bathrooms: property.bathrooms.toString(),
        sqft: property.sqft.toString(),
        building_name: property.building_name || "",
        address: property.address,
        amenities: property.amenities.join(", "),
        images: property.images.join(", "),
        featured: property.featured
      });
    } else {
      setFormData({
        title: "", description: "", price: "", location: "", market: "nyc",
        bedrooms: "", bathrooms: "", sqft: "", building_name: "", address: "",
        amenities: "", images: "", featured: false
      });
    }
  }, [property, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const data = {
      ...formData,
      price: parseFloat(formData.price),
      bedrooms: parseInt(formData.bedrooms),
      bathrooms: parseFloat(formData.bathrooms),
      sqft: parseInt(formData.sqft),
      amenities: formData.amenities.split(",").map(a => a.trim()).filter(Boolean),
      images: formData.images.split(",").map(i => i.trim()).filter(Boolean)
    };

    try {
      if (property) {
        await axios.put(`${API}/properties/${property.property_id}`, data, { withCredentials: true });
        toast.success("Property updated");
      } else {
        await axios.post(`${API}/properties`, data, { withCredentials: true });
        toast.success("Property created");
      }
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error("Failed to save property");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="btn-primary rounded-sm" data-testid="add-property-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Property
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{property ? "Edit Property" : "Add New Property"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Price</label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Market</label>
              <Select value={formData.market} onValueChange={(v) => setFormData({ ...formData, market: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nyc">New York City</SelectItem>
                  <SelectItem value="miami">Miami</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bedrooms</label>
              <Input
                type="number"
                value={formData.bedrooms}
                onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bathrooms</label>
              <Input
                type="number"
                step="0.5"
                value={formData.bathrooms}
                onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Square Feet</label>
              <Input
                type="number"
                value={formData.sqft}
                onChange={(e) => setFormData({ ...formData, sqft: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Building Name</label>
              <Input
                value={formData.building_name}
                onChange={(e) => setFormData({ ...formData, building_name: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Amenities (comma-separated)</label>
              <Input
                value={formData.amenities}
                onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                placeholder="Pool, Gym, Concierge"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Image URLs (comma-separated)</label>
              <Input
                value={formData.images}
                onChange={(e) => setFormData({ ...formData, images: e.target.value })}
              />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
              />
              <label htmlFor="featured" className="text-sm">Featured Property</label>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="btn-primary" disabled={loading}>
              {loading ? "Saving..." : "Save Property"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
